import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingFlatRoundedIcon from '@mui/icons-material/TrendingFlatRounded';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import { CardShell, asText } from './shared';
import Markdown from './Markdown';
import { canSendFromComponent, sendFromComponent } from './actions';

export interface MetricsSpec {
  tipo: 'metricas';
  titulo?: string;
  itens?: { rotulo?: unknown; valor?: unknown; unidade?: unknown; variacao?: unknown; tendencia?: unknown; nota?: unknown }[];
  fonte?: string;
}

export interface TabsSpec {
  tipo: 'abas';
  titulo?: string;
  abas?: { rotulo?: unknown; conteudo?: unknown }[];
}

export interface SuggestionsSpec {
  tipo: 'sugestoes';
  titulo?: string;
  opcoes?: { rotulo?: unknown; mensagem?: unknown }[];
}

const TREND = {
  alta: TrendingUpRoundedIcon,
  baixa: TrendingDownRoundedIcon,
  estavel: TrendingFlatRoundedIcon,
} as Record<string, typeof TrendingUpRoundedIcon>;

/** Números-chave lado a lado. A variação não ganha cor de "bom/ruim": inflação
 *  em alta e PIB em alta pedem cores opostas, e o modelo não tem como saber. */
export function Metrics({ spec }: { spec: MetricsSpec }) {
  const items = (spec.itens || []).filter((it) => it && it.valor !== undefined);
  if (!items.length) return null;
  return (
    <CardShell title={spec.titulo} source={spec.fonte} label="metricas">
      <MetricGrid>
        {items.map((it, i) => {
          const Trend = TREND[asText(it.tendencia)];
          return (
            <Metric key={i}>
              <MetricLabel>{asText(it.rotulo)}</MetricLabel>
              <MetricValue>
                {asText(it.valor)}
                {it.unidade !== undefined && <small> {asText(it.unidade)}</small>}
              </MetricValue>
              {(it.variacao !== undefined || Trend) && (
                <MetricDelta>
                  {Trend && <Trend style={{ fontSize: 15 }} />}
                  {it.variacao !== undefined && asText(it.variacao)}
                </MetricDelta>
              )}
              {it.nota !== undefined && <MetricNote>{asText(it.nota)}</MetricNote>}
            </Metric>
          );
        })}
      </MetricGrid>
    </CardShell>
  );
}

export function Tabs({ spec }: { spec: TabsSpec }) {
  const tabs = (spec.abas || []).filter((t) => t && asText(t.rotulo));
  const [active, setActive] = useState(0);
  if (!tabs.length) return null;
  const current = Math.min(active, tabs.length - 1);
  return (
    <CardShell title={spec.titulo} label="abas">
      <TabBar role="tablist">
        {tabs.map((t, i) => (
          <TabButton key={i} type="button" role="tab" aria-selected={i === current} $active={i === current}
            onClick={() => setActive(i)}>
            {asText(t.rotulo)}
          </TabButton>
        ))}
      </TabBar>
      <TabPanel role="tabpanel" key={current}>
        <Markdown>{asText(tabs[current].conteudo)}</Markdown>
      </TabPanel>
    </CardShell>
  );
}

/** Próximos passos: cada botão envia a mensagem como se o usuário a digitasse.
 *  Fora do chat (conversa compartilhada) ficam desativados. */
export function Suggestions({ spec }: { spec: SuggestionsSpec }) {
  const options = (spec.opcoes || []).filter((o) => o && asText(o.rotulo)).slice(0, 6);
  const [sent, setSent] = useState<number | null>(null);
  if (!options.length) return null;
  const enabled = canSendFromComponent();
  return (
    <SuggestionRow data-genui="sugestoes" aria-label={spec.titulo || 'Sugestões de próximos passos'}>
      {options.map((o, i) => (
        <Chip key={i} type="button" disabled={!enabled} $sent={sent === i}
          onClick={() => { if (sendFromComponent(asText(o.mensagem) || asText(o.rotulo))) setSent(i); }}>
          {asText(o.rotulo)}
          <NorthEastRoundedIcon style={{ fontSize: 13, opacity: 0.6 }} />
        </Chip>
      ))}
    </SuggestionRow>
  );
}

const accent = ({ theme }: any) => theme.prompt_accent_color || '#1a3c34';
const edge = ({ theme }: any) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.09)');

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
`;

const Metric = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${edge};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fff')};
`;

const MetricLabel = styled.div`
  font-size: 0.76rem;
  opacity: 0.7;
`;

const MetricValue = styled.div`
  font-size: 1.45rem;
  font-weight: 600;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
  small { font-size: 0.8rem; font-weight: 500; opacity: 0.7; }
`;

const MetricDelta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.78rem;
  font-weight: 500;
  opacity: 0.8;
`;

const MetricNote = styled.div`
  font-size: 0.72rem;
  opacity: 0.55;
  margin-top: 2px;
`;

const TabBar = styled.div`
  display: flex;
  gap: 2px;
  overflow-x: auto;
  border-bottom: 1px solid ${edge};
  margin-bottom: 10px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const TabButton = styled.button<{ $active: boolean }>`
  all: unset;
  flex-shrink: 0;
  padding: 7px 12px;
  font-size: 0.84rem;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  opacity: 0.65;
  &:hover { opacity: 1; }
  ${({ $active }) => $active && css`
    opacity: 1;
    border-bottom-color: ${accent};
    color: ${accent};
  `}
  &:focus-visible { outline: 2px solid ${accent}; border-radius: 4px; }
`;

const TabPanel = styled.div`
  animation: genuiFade 200ms ease-out;
  @keyframes genuiFade { from { opacity: 0; } to { opacity: 1; } }
`;

const SuggestionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0 4px;
`;

const Chip = styled.button<{ $sent: boolean }>`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 18px;
  font-family: 'Sora', 'Lexend', sans-serif;
  font-size: 0.84rem;
  line-height: 1.3;
  cursor: pointer;
  border: 1px solid ${edge};
  transition: border-color 150ms ease, background 150ms ease;
  &:hover:not(:disabled) {
    border-color: ${accent};
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(93,168,138,0.08)' : 'rgba(26,60,52,0.04)')};
  }
  &:disabled { cursor: default; opacity: 0.5; }
  &:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }
  ${({ $sent }) => $sent && css`border-color: ${accent};`}
`;
